import React, { useContext, useEffect, useState } from 'react';
import Breadcrumbs, { BreadcrumbsItem } from '../../template/MainTheme/components/Breadcrumbs';
import QuoteFilter from './components/tables/QuoteFilter';
import QuoteDataTable from './components/tables/QuoteDataTable';
import { Quote, QuoteFilters } from '../../app/models/Quote';
import moment from 'moment';
import { QuoteStatus } from '../../app/models/QuoteStatus';
import useQuoteService from '../../app/services/hooks/useQuoteService';
import { ServiceResponse } from '../../app/services/shared/interfaces';
import DefaultModal from '../../components/default/DefaultModal';
import { toast } from 'react-toastify';
import QuoteCreate from './components/QuoteCreate';
import DefaultCard from '../../components/default/DefaultCard';
import ButtonCreate from '../../components/buttons/ButtonCreate';
import { useHistory } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import { AppContext } from '../../contexts/AppContext';
import useReactConfirmAlert from '../../hooks/useReactConfirmAlert';
import useNavigationPage from '../../hooks/useNavigationPage';

const breadcrumbs: BreadcrumbsItem[] = [
    {
        name: 'Oferta',
        url: '/quotes',
        isActive: true
    }
];

const Quotes = () => {
    const {
        getAllQuotes,
        getExcelAllQuotes,
        fetchingGetQuotes,
        deleteQuote,
        repeatQuote,
        getDocumentQuote
    } = useQuoteService();

    const [quotes, setQuotes] = useState<Quote[]>([]);
    const [statuses, setStatuses] = useState<QuoteStatus[]>([]);
    const [showingCreate, setShowingCreate] = useState(false);
    const [totalRows, setTotalRows] = useState<number>(0);
    const { auth } = useContext(AuthContext);
    const { showLoading, hideLoading, changeAnimation } = useContext(AppContext);
    const { navigationPage } = useNavigationPage();

    const [filter, setFilter] = useState<QuoteFilters>({
        page: 1,
        per_page: 10,
        sort: 'id',
        order: 'desc'
    });

    // const [filter, setFilter] = useState<QuoteFilters>({
    //     start_date: moment().subtract(5, 'years').format('YYYY-MM-DD'),
    //     end_date: moment().add(1, 'months').format('YYYY-MM-DD'),
    //     quote_id: null,
    //     client: '',
    //     quote_status: ''
    // });

    useEffect(() => {
        reloadTable();
    }, [filter.page, filter.per_page, filter.sort, filter.order]);

    const reloadTable = () => {
        getAllQuotes(filter, {
            onSuccess: (response: ServiceResponse) => {
                setQuotes(response.data.quotes);
                setTotalRows(response.data.total_rows);
                setStatuses(response.data.quote_statuses);
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message, {
                    autoClose: 2500
                });
                if (response.data.to_dashboard) {
                    navigationPage('/');
                }
            }
        });
    };

    const destroy = (quoteId: number) => {
        const _text = 'Está a punto de eliminar la oferta #' + quoteId;

        useReactConfirmAlert().requestConfirmation({
            title: '¿Estás seguro?',
            message: _text,
            buttons: {
                confirmButton: {
                    label: 'Si, eliminar',
                    onClick: () => {
                        if (showLoading) showLoading('loading', 'Eliminando oferta...');
                        deleteQuote(quoteId, {
                            onSuccess: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();

                                useReactConfirmAlert().successAlert({
                                    title: 'Éxito',
                                    message: response.message
                                });

                                reloadTable();
                            },
                            onError: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();

                                useReactConfirmAlert().errorAlert({
                                    title: 'Error',
                                    message: response.message
                                });
                            }
                        });
                    }
                },
                cancelButton: {
                    label: 'No, cancelar',
                    onClick: () => {
                        setTimeout(() => {
                            useReactConfirmAlert().infoAlert({
                                title: 'Cancelado',
                                message: 'La oferta no se ha eliminado.'
                            });
                        }, 0);
                    }
                }
            }
        });
    };

    const repeat = (quoteId: number) => {
        const _text = 'Está a punto de duplicar la Oferta #' + quoteId;

        useReactConfirmAlert().requestConfirmation({
            title: '¿Estás seguro?',
            message: _text,
            buttons: {
                confirmButton: {
                    label: 'Si, duplicar',
                    onClick: () => {
                        if (showLoading) showLoading('loading', 'Duplicando oferta...');
                        repeatQuote(quoteId, {
                            onSuccess: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();

                                useReactConfirmAlert().successAlert({
                                    title: 'Éxito',
                                    message: response.message
                                });

                                reloadTable();
                            },
                            onError: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();

                                useReactConfirmAlert().errorAlert({
                                    title: 'Error',
                                    message: response.message
                                });
                            }
                        });
                    }
                },
                cancelButton: {
                    label: 'No, cancelar',
                    onClick: () => {
                        setTimeout(() => {
                            useReactConfirmAlert().infoAlert({
                                title: 'Cancelado',
                                message: 'La oferta no se ha repetido.'
                            });
                        }, 0);
                    }
                }
            }
        });
    };

    const document = (quoteId: number) => {
        getDocumentQuote(quoteId, {});
    };

    const downloadExcel = () => {
        getExcelAllQuotes(filter);
    };

    const showCreate = () => {
        setShowingCreate(true);
    };

    const hideCreate = () => {
        setShowingCreate(false);
    };

    const history = useHistory();

    const edit = (id?: number) => {
        history.push('/quotes/' + id + '/edit');
    };

    const show = (id?: number) => {
        history.push('/quotes/' + id + '/show');
    };

    return (
        <>
            <Breadcrumbs pageSection="Ofertas" breadcrumbs={breadcrumbs} />
            <DefaultCard>
                <div className="row">
                    <div className="col-12">
                        <div className="row">
                            <div className="col">
                                <ButtonCreate callbackCreate={showCreate} title="Nueva Oferta" />
                            </div>
                        </div>
                        <div className="row mt-3">
                            <div className="col">
                                <QuoteFilter filter={filter} setFilter={setFilter} onSearch={reloadTable}/>
                            </div>
                            <div className="col-auto" style={{ paddingTop: 22 }}>
                                <button className="btn btn-success btn-sm" onClick={downloadExcel}>
                                    <i className="fa fa-file-excel" /> Descargar
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="col-12">
                        <QuoteDataTable
                            quotes={quotes}
                            statuses={statuses}
                            totalRows={totalRows}
                            loading={fetchingGetQuotes}
                            destroy={destroy}
                            edit={edit}
                            show={show}
                            repeat={repeat}
                            documentQuote={document}
                            filter={filter}
                            setFilter={setFilter}
                            callbackSuccess={reloadTable}
                        />
                    </div>
                </div>
            </DefaultCard>
            {showingCreate ? (
                <DefaultModal
                    show={showingCreate}
                    handleClose={hideCreate}
                    title="Crear Oferta"
                    backdrop={true}
                    showFooter={false}
                >
                    <QuoteCreate
                        onSaved={() => {
                            reloadTable();
                            hideCreate();
                        }}
                        onCancel={hideCreate}
                        onError={hideCreate}
                    />
                </DefaultModal>
            ) : null}
        </>
    );
};

export default Quotes;
